import React from 'react';
import { graphql } from 'relay-runtime';
import { useFragment } from 'utils/graphClient';
import {
  HStack,
  IconButton,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from '@cardboard-ui/react';
import { Owner_ownership$key } from './__generated__/Owner_ownership.graphql';
import { UserHandle } from 'components/UserHandle';
import { RemoveUserIcon } from 'components/icons';
import { t } from '@lingui/macro';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { notEmpty } from 'utils/NotEmptyFilter';

const ownershipFragment = graphql`
  fragment Owner_ownership on Ownership {
    member {
      id
      name
      ...UserHandle_member
    }
    ownerOf {
      edges {
        node {
          ... on Space {
            name
          }
          ... on Event {
            name
          }
          ... on Folder {
            name
          }
        }
      }
    }
  }
`;

interface OwnerProps {
  ownership: Owner_ownership$key;
  canRemoveOwner?: boolean;
  onRemoveOwner?: (onSuccess: () => void) => void;
  isRemoveOwnerLoading?: boolean;
  isImplicit?: boolean;
}

export const Owner = ({
  ownership,
  canRemoveOwner,
  onRemoveOwner,
  isRemoveOwnerLoading,
  isImplicit,
}: OwnerProps) => {
  const { member, ownerOf } = useFragment(ownershipFragment, ownership);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const ownables = ownerOf.edges?.filter(notEmpty) || [];

  const subText =
    isImplicit && ownables.length > 0
      ? t`Owner of ${ownables[0].node?.name}`
      : t`Owner`;
  return (
    <>
      <HStack>
        <Stack>
          <UserHandle
            showAvatar
            member={member}
            linkToProfile
            nameProps={{
              fontWeight: 'bold',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0,
            }}
          >
            {<Text fontWeight="normal">{subText}</Text>}
          </UserHandle>
        </Stack>
        <Spacer />
        {canRemoveOwner && onRemoveOwner && (
          <IconButton
            aria-label={t`Remove owner`}
            icon={<RemoveUserIcon />}
            onClick={onOpen}
          />
        )}
      </HStack>
      {canRemoveOwner && onRemoveOwner && (
        <ConfirmationDialog
          action={{
            name: t`Remove owner`,
            onAction: () => {
              onRemoveOwner(onClose);
            },
            inProgress: isRemoveOwnerLoading,
          }}
          isOpen={isOpen}
          onClose={onClose}
          confirmationText={t`Remove owner`}
        >
          {t`Are you sure you want to remove ${member.name} as owner?`}
        </ConfirmationDialog>
      )}
    </>
  );
};
